import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { SearchInput } from "@/components/ui/search-input";
import { NAMESPACES } from "@/i18n/constants";
import type { StreamVisibility } from "../spec/stream-visibility";
import { AvailableList } from "./available-list";
import { filterPickerStreams } from "./filter-picker-streams";
import { groupPickerStreams } from "./group-picker-streams";
import { type PickerFilter, PickerFilterMenu } from "./picker-filter";
import { StatusLine, usePickerLabels } from "./picker-rows";
import type { PickerStream } from "./picker-stream";
import type { CatalogStatus } from "./use-picker-catalog";

export function AvailableTab({
    unplotted,
    status,
    onAdd,
    filter,
    onFilterChange,
    isAdmin,
    initialQuery = "",
}: {
    unplotted: readonly PickerStream[];
    status: CatalogStatus;
    onAdd: (streamId: number) => void;
    filter: PickerFilter;
    onFilterChange: (filter: PickerFilter) => void;
    isAdmin: boolean;
    initialQuery?: string;
}) {
    const { t } = useTranslation(NAMESPACES.FEATURES.DATA_VIEWER, { keyPrefix: "stream-picker" });
    const { t: tMessages } = useTranslation(NAMESPACES.FEATURES.DATA_VIEWER, { keyPrefix: "messages" });
    const [query, setQuery] = useState(initialQuery);
    const labels = usePickerLabels();

    const { groups, matchCount, filteredOut } = useMemo(() => {
        const matches = filterPickerStreams(
            unplotted.filter((stream) => filter[stream.visibility]),
            query,
        );
        // Only looked for when nothing else matched, to offer the filtered-out streams instead.
        const outside =
            matches.streams.length > 0
                ? []
                : filterPickerStreams(
                      unplotted.filter((stream) => !filter[stream.visibility]),
                      query,
                  ).streams;
        return {
            groups: groupPickerStreams(matches.streams, labels, matches.rankById),
            matchCount: matches.streams.length,
            filteredOut: outside,
        };
    }, [unplotted, query, labels, filter]);

    const searching = query.trim().length > 0;
    const showFilteredOut = () => {
        const next: Record<StreamVisibility, boolean> = { ...filter };
        filteredOut.forEach((stream) => {
            next[stream.visibility] = true;
        });
        onFilterChange(next);
    };

    let emptyText = t("none-left");
    if (searching) emptyText = t("no-matches", { query: query.trim() });
    else if (filteredOut.length > 0) emptyText = t("filtered-empty");

    let body: React.ReactNode;
    if (status === "loading") {
        body = <StatusLine>{tMessages("loading")}</StatusLine>;
    } else if (status === "error") {
        body = <StatusLine>{tMessages("load-failed")}</StatusLine>;
    } else if (groups.length === 0) {
        body = (
            <div className={"flex flex-col items-center"}>
                <StatusLine>{emptyText}</StatusLine>
                {filteredOut.length > 0 && (
                    <Button onClick={showFilteredOut} size={"sm"} variant={"link"}>
                        {t("show-filtered-out", { count: filteredOut.length })}
                    </Button>
                )}
            </div>
        );
    } else {
        body = <AvailableList groups={groups} onAdd={onAdd} showVisibility={filter.diagnostic || filter.adminOnly} />;
    }

    return (
        <>
            <div className={"flex items-center gap-2 p-2.5"}>
                <SearchInput
                    aria-label={t("search-label")}
                    className={"flex-1"}
                    clearable
                    endText={searching && matchCount > 0 ? t("match-count", { count: matchCount }) : undefined}
                    onValueChange={setQuery}
                    placeholder={t("search-placeholder")}
                    value={query}
                />
                <PickerFilterMenu filter={filter} isAdmin={isAdmin} onFilterChange={onFilterChange} />
            </div>
            {body}
        </>
    );
}
